import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";

// Import from centralized model
import { SearchPlacesConfig, PlacePrediction, PlaceDetailsResult } from "../../model";

/**
 * Clean Google Places Autocomplete service
 * Uses only the new AutocompleteSuggestion API (recommended from March 2025)
 * No legacy AutocompleteService support
 */
@Injectable({
  providedIn: "root",
})
export class SearchPlacesAutocompleteService {
  private readonly DEFAULT_CONFIG: SearchPlacesConfig = {
    enabled: true,
    types: ["geocode"],
  };

  private config: SearchPlacesConfig = this.DEFAULT_CONFIG;

  private placesService: google.maps.places.PlacesService;

  constructor() {}

  /**
   * Initialize Google Places services
   * Assumes google.maps is already loaded globally
   */
  private initializeGoogleServices(): void {
    try {
      if (!this.placesService && typeof google !== "undefined" && google.maps && google.maps.places) {
        // Create a dummy div for PlacesService initialization
        const dummyDiv = document.createElement("div");
        this.placesService = new google.maps.places.PlacesService(dummyDiv);
      } else {
        console.warn("Google Maps Places API not available. SearchPlacesAutocompleteService will be disabled.");
      }
    } catch (error) {
      console.error("Failed to initialize Google Places services:", error);
    }
  }

  /**
   * Get place predictions from Google Places using new AutocompleteSuggestion API only
   * @param input Search input text
   * @returns Observable of place predictions
   */
  suggestFromPlaces(input: string): Observable<PlacePrediction[]> {
    this.initializeGoogleServices();
    if (!this.config.enabled || !input?.trim()) {
      return of([]);
    }

    // Only use new AutocompleteSuggestion API
    if (
      typeof google !== "undefined" &&
      google.maps &&
      google.maps.places &&
      (google.maps.places as any).AutocompleteService
    ) {
      return this.getPlacesSuggestions(input);
    } else {
      console.warn("Google Places AutocompleteSuggestion API not available. Please upgrade to the new API.");
      return of([]);
    }
  }

  /**
   * Use new AutocompleteSuggestion API (recommended from March 2025)
   * @param input Search input text
   * @returns Observable of place predictions
   */
  private getPlacesSuggestions(input: string): Observable<PlacePrediction[]> {
    const request = {
      input: input.trim(),
      componentRestrictions: this.config.componentRestrictions,
      types: this.config.types,
    };

    return new Observable<PlacePrediction[]>((observer) => {
      try {
        const autocomplete = new (google.maps.places as any).AutocompleteService();

        autocomplete.getPlacePredictions(request, (predictions: any, status: any) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            // 3 prediccions max
            const processedPredictions = predictions
              .slice(0, 3)
              .map((prediction: any) => this.processPrediction(prediction, input));

            observer.next(processedPredictions);
          } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            observer.next([]);
          } else {
            console.warn("Google Places AutocompleteSuggestion error:", status);
            observer.next([]);
          }
          observer.complete();
        });
      } catch (error) {
        console.error("Error in getPlacesSuggestions:", error);
        observer.next([]);
        observer.complete();
      }
    }).pipe(
      catchError((error) => {
        console.error("Error in getPlacesSuggestions:", error);
        return of([]);
      }),
    );
  }

  /**
   * Get detailed place information
   * @param placeId Google Place ID
   * @returns Observable of place details
   */
  getPlaceDetails(placeId: string): Observable<PlaceDetailsResult | null> {
    this.initializeGoogleServices();
    if (!this.placesService || !placeId) {
      return of(null);
    }

    const request: google.maps.places.PlaceDetailsRequest = {
      placeId,
      fields: ["place_id", "formatted_address", "types", "geometry"],
    };

    return new Observable<PlaceDetailsResult | null>((observer) => {
      this.placesService.getDetails(request, (result, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && result) {
          const processedResult = this.processPlaceDetails(result);
          observer.next(processedResult);
        } else {
          console.warn("Google Places getDetails error:", status);
          observer.next(null);
        }
        observer.complete();
      });
    }).pipe(
      catchError((error) => {
        console.error("Error in getPlaceDetails:", error);
        return of(null);
      }),
    );
  }

  /**
   * Process raw Google prediction and calculate confidence score
   * @param prediction Raw Google prediction
   * @param input Original input text
   * @returns Processed prediction with confidence
   */
  private processPrediction(prediction: any, input: string): PlacePrediction {
    const confidence = this.calculatePredictionConfidence(prediction, input);

    return {
      placeId: prediction.place_id,
      text: prediction.description,
      structured_formatting: prediction.structured_formatting || {
        main_text: prediction.description,
        secondary_text: "",
      },
      types: prediction.types || [],
      confidence,
    };
  }

  /**
   * Calculate confidence score for a prediction based on types and text match
   * @param prediction Google prediction
   * @param input Original input
   * @returns Confidence score (0-1)
   */
  private calculatePredictionConfidence(prediction: any, input: string): number {
    let confidence = 0.5; // Base confidence

    // Boost confidence based on place types
    const types = prediction.types || [];
    if (types.includes("street_address")) confidence += 0.3;
    else if (types.includes("route")) confidence += 0.25;
    else if (types.includes("establishment")) confidence += 0.2;
    else if (types.includes("locality")) confidence += 0.15;
    else if (types.includes("administrative_area_level_1")) confidence += 0.1;
    else if (types.includes("country")) confidence += 0.05;

    // Boost confidence based on text match quality
    const mainText = (prediction.structured_formatting?.main_text || prediction.description || "").toLowerCase();
    const inputLower = input.toLowerCase();

    if (mainText.startsWith(inputLower)) {
      confidence += 0.2;
    } else if (mainText.includes(inputLower)) {
      confidence += 0.1;
    }

    // Cap confidence at 1.0
    return Math.min(confidence, 1.0);
  }

  /**
   * Process Google PlaceResult into simplified PlaceDetailsResult
   * @param placeResult Google PlaceResult
   * @returns Processed place details
   */
  private processPlaceDetails(placeResult: google.maps.places.PlaceResult): PlaceDetailsResult {
    const result: PlaceDetailsResult = {
      place_id: placeResult.place_id || "",
      formatted_address: placeResult.formatted_address || "",
      name: placeResult.name,
      types: placeResult.types || [],
      placeResult,
    };

    // Extract location if available
    if (placeResult.geometry?.location) {
      const lat = placeResult.geometry.location.lat();
      const lng = placeResult.geometry.location.lng();
      result.location = [lat, lng];
    }

    // Extract geometry/bounds if available (for administrative areas)
    if (placeResult.geometry && (placeResult.geometry as any).bounds) {
      // Convert bounds to a simple bounding box geometry
      const bounds = (placeResult.geometry as any).bounds;
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      // Create a simple polygon WKT for the bounding box
      result.geometry = `POLYGON((${sw.lng()} ${sw.lat()}, ${ne.lng()} ${sw.lat()}, ${ne.lng()} ${ne.lat()}, ${sw.lng()} ${ne.lat()}, ${sw.lng()} ${sw.lat()}))`;
    } else if (placeResult.geometry && (placeResult.geometry as any).viewport) {
      // Fallback to viewport if bounds not available
      const viewport = (placeResult.geometry as any).viewport;
      const sw = viewport.getSouthWest();
      const ne = viewport.getNorthEast();

      result.geometry = `POLYGON((${sw.lng()} ${sw.lat()}, ${ne.lng()} ${sw.lat()}, ${ne.lng()} ${ne.lat()}, ${sw.lng()} ${ne.lat()}, ${sw.lng()} ${sw.lat()}))`;
    }

    return result;
  }
}
